// src/components/TestimonialsCarousel.js

import React, { useState } from 'react';
import Slider from 'react-slick';

// Import slick-carousel css files
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// Removed 'Fade' from imports because it was unused
import { Box, Container, Typography, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';

// --- Color Palette ---
const TEMPLE_GOLD = '#F7F2E9';
const CINNAMON_BROWN = '#A65F34';
const PEACOCK_TEAL = '#007D8C';
const INACTIVE_DOT_GRAY = '#D1CFC7';

// --- Testimonial Data ---
const testimonials = [
  {
    quote: "“Bella Ceylon Voyages didn’t just show us Sri Lanka, they helped us feel it. Every detail was personal, every experience authentic. It was the journey of a lifetime.”",
    attribution: "— Emily Carter, United Kingdom"
  },
  {
    quote: "“Our guide, Saman, was the heart of our journey. He didn’t just take us to the sights; he shared stories that brought the history of Cylon to life. We saw and experienced things we never would have found on our own.”",
    attribution: "— The Chen Family, Singapore"
  },
  {
    quote: "“The planning process was seamless. The Bella Ceylon Voyages team listened to our every wish and crafted an itinerary that perfectly balanced adventure with relaxation. It felt less like a package tour and more like our own personal discovery.”",
    attribution: "— David & Sarah, USA"
  },
  {
    quote: "“Traveling with two young children can be stressful, but Bella Ceylon Voyages took care of everything. From the family-friendly hotels to activities that captivated our kids, it was the most relaxing and memorable family vacation we've ever had.”",
    attribution: "— Mark & Helen Johnson, Australia"
  },
];

// --- Custom Arrow Components ---
const CustomArrow = ({ direction, onClick, isVisible }) => (
  <IconButton
    onClick={onClick}
    sx={{
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      ...(direction === 'left' ? { left: 16 } : { right: 16 }),
      zIndex: 2,
      color: CINNAMON_BROWN,
      backgroundColor: 'rgba(255, 255, 255, 0.5)',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
      },
      // The core of the hover effect: opacity tied to parent state
      opacity: isVisible ? 1 : 0,
      transition: 'opacity 0.3s ease-in-out',
    }}
  >
    {direction === 'left' ? <ChevronLeft fontSize="large" /> : <ChevronRight fontSize="large" />}
  </IconButton>
);


// --- Main Testimonial Component ---
const TestimonialsCarousel = () => {
  const [isHovered, setIsHovered] = useState(false);

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 1500, // Speed of the fade transition in ms
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 8000, // Time between slides in ms
    fade: true,
    pauseOnHover: true,
    nextArrow: <CustomArrow direction="right" isVisible={isHovered} />,
    prevArrow: <CustomArrow direction="left" isVisible={isHovered} />,
    // Custom dots styling
    appendDots: dots => (
      <Box component="ul" sx={{ bottom: '-40px', m: 0, p: 0 }}>
        {dots}
      </Box>
    ),
  };

  return (
    <Box
      component="section"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        backgroundColor: TEMPLE_GOLD,
        py: { xs: 10, md: 14 },
        textAlign: 'center',
        position: 'relative', // Needed for absolute positioning of arrows
        overflow: 'hidden',
        // --- Slick Carousel Overrides ---
        '.slick-slider': {
          zIndex: 1,
        },
        '.slick-dots li button:before': {
          fontSize: '10px',
          color: INACTIVE_DOT_GRAY,
          opacity: 1,
        },
        '.slick-dots li.slick-active button:before': {
          color: PEACOCK_TEAL,
          opacity: 1,
        },
        '.slick-arrow': {
            // Hides the default arrows, we use our custom ones
            display: 'none !important',
        }
      }}
    >
      <Container maxWidth="md">
        <Typography
          variant="h4"
          component="h2"
          sx={{
            fontFamily: '"Lato", "Helvetica", "Arial", sans-serif',
            fontWeight: 700,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '1.5px',
            mb: 8,
          }}
        >
          Stories from Our Travelers
        </Typography>

        <Slider {...sliderSettings}>
          {testimonials.map((testimonial, index) => (
            <div key={index}> {/* Wrapper div required by react-slick */}
                <Box sx={{ px: { xs: 2, sm: 4 } }}> {/* Adds horizontal padding inside the slide */}
                  <Typography
                    component="blockquote"
                    sx={{
                      fontFamily: '"Cormorant Garamond", "Georgia", serif',
                      fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.8rem' },
                      fontStyle: 'italic',
                      fontWeight: 500,
                      lineHeight: 1.4,
                      color: 'text.primary',
                      maxWidth: '800px',
                      p: 0,
                      m: 0, 
                      // Fixed Duplicate Keys here:
                      mb: 4, 
                      mx: 'auto',
                      minHeight: { xs: 240, sm: 220, md: 250 } // Reserve space to prevent layout jump
                    }}
                  >
                    {testimonial.quote}
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily: '"Lato", "Helvetica", "Arial", sans-serif',
                      fontSize: '1rem',
                      fontWeight: 400,
                      color: 'text.secondary',
                    }}
                  >
                    {testimonial.attribution}
                  </Typography>
                </Box>
            </div>
          ))}
        </Slider>
      </Container>
    </Box>
  );
};

export default TestimonialsCarousel;